import { generateTextFromHandlebarsTemplate } from 'lib/orders/dynamic-sections';
import useAsyncEffect from 'lib/frontend/hooks/useAsyncEffect';
import { Ad } from 'lib/types/ad';
import { ColumnService } from 'lib/services/directory';
import { OrderFilingType } from 'lib/types/filingType';
import { EOrganization, ERef } from 'lib/types';
import { safeGetModelFromRef } from 'lib/model/getModel';
import { FilingTypeModel } from 'lib/model/objects/filingTypeModel';
import { OrganizationModel } from 'lib/model/objects/organizationModel';
import { wrapSuccess } from 'lib/types/responses';
import useDebounce from 'lib/frontend/hooks/useDebounce';
import { safeStringify } from 'lib/utils/stringify';
import { getFirebaseContext } from 'utils/firebase';
import { AdEditorData } from '../steps/DraftContent/FormattedEditorWrapper';

type UseDynamicTitleProps<T extends Ad> = {
  filingType: ERef<OrderFilingType>;
  newspaper?: ERef<EOrganization>;
  adData: Partial<T>;
  onChange: (update: AdEditorData) => void;
};

export function useDynamicTitle<T extends Ad>({
  filingType,
  newspaper,
  adData,
  onChange
}: UseDynamicTitleProps<T>) {
  const debouncedAdData = useDebounce(adData, 300);

  useAsyncEffect({
    fetchData: async () => {
      const [filingTypeModelError, filingTypeModel] = await safeGetModelFromRef(
        FilingTypeModel,
        getFirebaseContext(),
        filingType
      );

      if (filingTypeModelError) {
        throw filingTypeModelError;
      }

      const [newspaperModelError, newspaperModel] = !newspaper
        ? wrapSuccess(null)
        : await safeGetModelFromRef(
            OrganizationModel,
            getFirebaseContext(),
            newspaper
          );

      if (newspaperModelError) {
        throw newspaperModelError;
      }

      const titleSection = filingTypeModel.modelData.dynamicSections?.find(
        section => section.id === 'title'
      );
      if (!titleSection) {
        return null;
      }

      const title = generateTextFromHandlebarsTemplate({
        template: titleSection.template || '',
        adContent: debouncedAdData,
        filingType: filingTypeModel.modelData,
        publisherOrganization: newspaperModel?.modelData
      });

      onChange({ title });
    },
    dependencies: [filingType.id, safeStringify(debouncedAdData.madlibData)],
    errorConfig: {
      service: ColumnService.ORDER_PLACEMENT,
      message: 'Failed to generate dynamic title',
      tags: { filingTypeId: filingType.id || '' }
    }
  });
}
